/**
 * IndexedDB-backed session store for audio recording.
 * Persists audio chunks across browser crashes and tab kills.
 */
import sessionId from "../session_id.js";

const DB_NAME = "meeting-recordings";
const DB_VERSION = 1;

class IndexedDBSession {
  constructor({ id, startedAt, status, store }) {
    this.id = id;
    this.startedAt = startedAt;
    this.status = status;
    this._store = store;
  }

  async addChunk(index, blob) {
    const db = await this._store.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("chunks", "readwrite");
      tx.objectStore("chunks").put({ sessionId: this.id, index, blob });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async chunks() {
    const db = await this._store.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("chunks", "readonly");
      const idx = tx.objectStore("chunks").index("sessionId");
      const request = idx.getAll(this.id);
      request.onsuccess = () => {
        resolve(request.result.sort((a, b) => a.index - b.index).map(c => c.blob));
      };
      request.onerror = () => reject(request.error);
    });
  }

  async duration() {
    const chunks = await this.chunks();
    return IndexedDBSession.formatDuration(chunks.length);
  }

  async update(attrs) {
    const db = await this._store.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("sessions", "readwrite");
      const store = tx.objectStore("sessions");
      const request = store.get(this.id);
      request.onsuccess = () => {
        const record = request.result;
        if (!record) {
          reject(new Error(`Session not found: ${this.id}`));
          return;
        }

        Object.assign(record, attrs);
        store.put(record);
      };
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => {
        Object.assign(this, attrs);
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  async delete() {
    const db = await this._store.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(["sessions", "chunks"], "readwrite");
      tx.objectStore("sessions").delete(this.id);

      const chunkStore = tx.objectStore("chunks");
      const request = chunkStore.index("sessionId").openCursor(this.id);
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) { cursor.delete(); cursor.continue(); }
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  static formatDuration(chunkCount) {
    const totalSeconds = chunkCount * 10;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
}

export default class IndexedDBSessionStore {
  constructor() {
    this.dbPromise = null;
  }

  async create() {
    const db = await this.openDB();
    const id = sessionId();
    const record = { id, startedAt: new Date().toISOString(), status: "recording" };

    return new Promise((resolve, reject) => {
      const tx = db.transaction("sessions", "readwrite");
      tx.objectStore("sessions").put(record);
      tx.oncomplete = () => resolve(new IndexedDBSession({ ...record, store: this }));
      tx.onerror = () => reject(tx.error);
    });
  }

  async all() {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction("sessions", "readonly");
      const request = tx.objectStore("sessions").getAll();
      request.onsuccess = () => {
        resolve(request.result.map(r => new IndexedDBSession({ ...r, store: this })));
      };
      request.onerror = () => reject(request.error);
    });
  }

  async close() {
    if (this.dbPromise) {
      const db = await this.dbPromise;
      db.close();
      this.dbPromise = null;
    }
  }

  // private

  openDB() {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        db.createObjectStore("sessions", { keyPath: "id" });

        const chunkStore = db.createObjectStore("chunks", { autoIncrement: true });
        chunkStore.createIndex("sessionId", "sessionId", { unique: false });
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    return this.dbPromise;
  }
}
